import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Send, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const waNumber = (t) => { const d = (t || "").replace(/\D/g, ""); return d.length === 8 ? `228${d}` : d; };
const dt = (s) => new Date(s).toLocaleString("fr-FR");

const SendLog = ({ sends }) => sends.length === 0 ? null : (
  <div className="border-t pt-3 mt-1" data-testid="send-log">
    <p className="text-xs font-semibold text-slate-500 mb-1">Envois précédents</p>
    <ul className="space-y-0.5 max-h-28 overflow-y-auto text-xs text-slate-600">
      {sends.slice().reverse().map((s, i) => <li key={i}>{dt(s.at)} · {s.canal === "email" ? "E-mail" : "WhatsApp"} · {s.to || "—"} · par {s.by}</li>)}
    </ul>
  </div>
);

export const SendReceipt = ({ t }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", note: "" });
  const [sends, setSends] = useState([]);
  const [busy, setBusy] = useState(false);
  const base = window.location.origin;
  const openDlg = async () => {
    setOpen(true);
    try { const r = await api.get(`/cash-transactions/${t.id}/recipient`); setF({ ...r.data.recipient, note: "" }); setSends(r.data.sends); } catch (e) { toast.error(apiErr(e)); }
  };
  const sendEmail = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await api.post(`/cash-transactions/${t.id}/send-email`, { to: f.email.trim(), name: f.name, note: f.note, base }); setSends([...sends, r.data.entry]); toast.success("Reçu envoyé par e-mail"); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  const sendWa = async (e) => {
    e.preventDefault();
    const win = window.open("", "_blank");
    setBusy(true);
    try {
      const r = await api.post(`/cash-transactions/${t.id}/whatsapp-link`, { base, to: f.phone });
      const url = `https://wa.me/${waNumber(f.phone)}?text=${encodeURIComponent(r.data.message)}`;
      if (win) win.location.href = url; else window.location.href = url;
      setSends([...sends, r.data.entry]); toast.success("WhatsApp ouvert avec le message prêt");
    } catch (err) { if (win) win.close(); toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="ghost" size="icon" title="Envoyer le reçu" onClick={openDlg} data-testid={`tx-send-${t.id}`}><Send className="h-4 w-4 text-slate-400 hover:text-brand-600" /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="send-receipt-dialog">
          <DialogHeader><DialogTitle className="font-head">Envoyer le reçu <span className="font-mono text-sm text-brand-700">{t.numero}</span></DialogTitle></DialogHeader>
          <div><Label className="text-xs">Nom du destinataire</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" data-testid="send-name" /></div>
          <Tabs defaultValue="email">
            <TabsList className="grid grid-cols-2 w-full"><TabsTrigger value="email" data-testid="send-tab-email"><Mail className="h-4 w-4 mr-1.5" />E-mail</TabsTrigger><TabsTrigger value="whatsapp" data-testid="send-tab-whatsapp"><MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp</TabsTrigger></TabsList>
            <TabsContent value="email">
              <form onSubmit={sendEmail} className="space-y-3">
                <div><Label className="text-xs">Adresse e-mail</Label><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1" data-testid="send-email-input" /></div>
                <div><Label className="text-xs">Message (facultatif)</Label><Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="mt-1" data-testid="send-note" /></div>
                <p className="text-[11px] text-slate-500">Le reçu PDF est joint à l'e-mail, avec votre signature e-mail.</p>
                <Button type="submit" disabled={busy} className="w-full bg-brand-600 hover:bg-brand-700 text-white" data-testid="send-email-submit">{busy ? "Envoi…" : "Envoyer par e-mail"}</Button>
              </form>
            </TabsContent>
            <TabsContent value="whatsapp">
              <form onSubmit={sendWa} className="space-y-3">
                <div><Label className="text-xs">Numéro WhatsApp</Label><Input required value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Ex. 90 00 00 00 ou 22890000000" className="mt-1" data-testid="send-phone-input" /></div>
                <p className="text-[11px] text-slate-500">WhatsApp s'ouvre avec un message prêt : référence, montant et lien de téléchargement du reçu (valable 30 jours).</p>
                <Button type="submit" disabled={busy} className="w-full bg-[#25D366] hover:bg-[#1ebe5a] text-white" data-testid="send-wa-submit">{busy ? "Préparation…" : "Ouvrir WhatsApp"}</Button>
              </form>
            </TabsContent>
          </Tabs>
          <SendLog sends={sends} />
        </DialogContent>
      </Dialog>
    </>
  );
};
